// src/components/navigate/NavigateToCard.tsx
//
// One-tap walking-directions sheet. Mounts inline in the chat
// when the AI emits a [NAVIGATE_TO] block, or when a pre-armed
// reveal chip is tapped.
//
// Architecture (post-fix — see CHANGE LOG below):
//
//   1. Inline teaser card — appears in the message stream where
//      the block was. Always visible. Shows destination name,
//      optional rationale, and a primary "Take me there" button
//      that opens the bottom sheet. A secondary "Google Maps"
//      ghost button jumps to a maps deep link without opening
//      the sheet.
//
//   2. Bottom sheet (Drawer anchor='bottom') — slides up from
//      the bottom edge. Matches the app's existing bottom-sheet
//      convention (WalkingTourView's waypoint picker,
//      MapExplorationView's pin detail): rounded TOP corners,
//      maxHeight 85vh, drag-affordance bar at the top. Live map
//      via MiniMap with walking polyline. Footer has "Open in
//      Google Maps" breakout.
//
//   3. Dismiss — swipe-to-close, tap-outside, or tap the close X
//      in the header all dismiss the sheet.
//
// CHANGE LOG (Phase 5D-fix):
//
//   - Switched anchor from 'top' to 'bottom'. The previous
//     top-anchored "pull down a map" pattern was a new convention
//     not used elsewhere in the app. Existing convention is
//     anchor='bottom' with rounded top corners (matches MUI/iOS
//     bottom-sheet idiom and the app's existing patterns).
//
//   - Switched dvh units to vh. Dynamic viewport units don't
//     resolve consistently on older Safari, which was collapsing
//     the map body height to 0 → blank map. vh is universally
//     supported and the visible-keyboard offset isn't a concern
//     here (no text input inside the sheet).
//
//   - Map body now has an explicit pixel-based height computed
//     from window.innerHeight rather than relying on flex: 1.
//     MUI Drawer Paper is not a flex container by default, so
//     flex: 1 wasn't expanding the middle box.
//
//   - Added the convention's drag-handle pill at the top of the
//     sheet for affordance consistency with other bottom sheets.
//
// Reuses without modification: MiniMap, useWalkingRoute,
// useLocation.

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Drawer,
  IconButton,
  Paper,
  useTheme,
} from '@mui/material';
import {
  Directions,
  Close,
  OpenInNew,
} from '@mui/icons-material';
import { MiniMap } from '../map/MiniMap';
import { useLocation } from '../../hooks/useLocation';
import { useWalkingRoute } from '../../hooks/useWalkingRoute';
import { useT } from '../../i18n/useT';
import type { NavigateTarget } from '../../utils/interactiveContent';

interface Props {
  target: NavigateTarget;
  /** Optional controlled-mode props. When provided, the parent
   *  owns the sheet's open state and the inline teaser card is
   *  hidden. Used by CollectionRevealCard which renders its own
   *  chip-style trigger to match the existing reveal UI. */
  open?: boolean;
  onClose?: () => void;
  /** When true, suppress the inline teaser entirely. Useful for
   *  controlled-mode embeds where the caller has its own
   *  trigger surface (e.g. a chip on a collection-reveal card). */
  hideTeaser?: boolean;
}

function formatDistance(m: number | null): string {
  if (m == null) return '—';
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

function formatDuration(s: number | null): string {
  if (s == null) return '—';
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

function buildMapsUrl(target: NavigateTarget): string {
  const base = 'https://www.google.com/maps/dir/?api=1';
  const dest = `&destination=${target.lat},${target.lng}`;
  const placeId = target.place_id
    ? `&destination_place_id=${encodeURIComponent(target.place_id)}`
    : '';
  return `${base}${dest}${placeId}&travelmode=walking`;
}

export const NavigateToCard: React.FC<Props> = ({
  target,
  open: controlledOpen,
  onClose: controlledOnClose,
  hideTeaser = false,
}) => {
  const t = useT();
  const theme = useTheme();

  // Controlled-mode detection: if parent passes `open`, they
  // control the sheet. Otherwise we maintain our own state.
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const sheetOpen = isControlled ? !!controlledOpen : internalOpen;

  const { location } = useLocation();
  const destCoord = useMemo(
    () => ({ lat: target.lat, lng: target.lng }),
    [target.lat, target.lng]
  );
  const userCoord = useMemo(
    () =>
      location
        ? { lat: location.latitude, lng: location.longitude }
        : null,
    [location?.latitude, location?.longitude]
  );

  const route = useWalkingRoute(destCoord, userCoord);

  // Compute the map body height from window.innerHeight in
  // PIXELS rather than relying on flex: 1 inside MUI Drawer
  // Paper (which is not a flex container by default). Falls
  // back to 480 on first render (before the layout effect runs)
  // so the map has SOMETHING to render against.
  const [mapBodyPx, setMapBodyPx] = useState<number>(480);
  useEffect(() => {
    const compute = () => {
      // 85vh total max, minus ~64px header, ~76px footer, ~12px
      // drag handle area. Cap at viewport - 200 for very small
      // screens.
      const vh = window.innerHeight;
      const target = Math.min(
        Math.round(vh * 0.85) - 152,
        vh - 200
      );
      setMapBodyPx(Math.max(280, target));
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('orientationchange', compute);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('orientationchange', compute);
    };
  }, []);

  const handleOpenSheet = useCallback(() => {
    if (isControlled) return;
    setInternalOpen(true);
  }, [isControlled]);
  const handleCloseSheet = useCallback(() => {
    if (isControlled) {
      controlledOnClose?.();
    } else {
      setInternalOpen(false);
    }
  }, [isControlled, controlledOnClose]);
  const handleOpenMaps = useCallback(() => {
    window.open(buildMapsUrl(target), '_blank', 'noopener,noreferrer');
  }, [target]);

  return (
    <>
      {/* Inline teaser — rendered only when not in controlled mode
          AND not explicitly hidden. The reveal-card path passes
          hideTeaser=true because it has its own chip trigger. */}
      {!hideTeaser && !isControlled && (
        <Paper
          elevation={0}
          sx={{
            mt: 1.5,
            mb: 0.5,
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            background: `linear-gradient(135deg, ${theme.palette.primary.light}14, ${theme.palette.background.paper})`,
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Directions sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, lineHeight: 1.25 }}
                >
                  {target.name}
                </Typography>
                {target.reasoning && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.25, lineHeight: 1.4 }}
                  >
                    {target.reasoning}
                  </Typography>
                )}
                {route.distanceM != null && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {formatDistance(route.distanceM)} ·{' '}
                    {formatDuration(route.durationS)} {t('walk')}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<Directions />}
                onClick={handleOpenSheet}
                disableElevation
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                  flex: 1,
                  minWidth: 140,
                }}
              >
                {t('Take me there')}
              </Button>
              <Button
                variant="text"
                size="small"
                startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                onClick={handleOpenMaps}
                sx={{
                  textTransform: 'none',
                  color: 'text.secondary',
                  borderRadius: 2,
                }}
              >
                {t('Google Maps')}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Bottom sheet — matches the app's existing convention
          (WalkingTourView's waypoint picker, MapExplorationView's
          pin detail). anchor='bottom', rounded top corners,
          drag-handle pill, maxHeight 85vh. */}
      <Drawer
        anchor="bottom"
        open={sheetOpen}
        onClose={handleCloseSheet}
        keepMounted={false}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '85vh',
            overflow: 'hidden',
            // Account for iOS bottom safe area so the footer
            // button isn't covered by the home indicator.
            pb: 'env(safe-area-inset-bottom, 0px)',
          },
        }}
      >
        {/* Drag handle pill — visual affordance for swipe-to-close
            and matches the app's other bottom sheets. Decorative;
            the actual swipe gesture is handled by MUI's Drawer. */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 1,
            pb: 0.5,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 4,
              borderRadius: 2,
              bgcolor: 'divider',
            }}
          />
        </Box>

        {/* Header row — title, distance/duration, close X */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {target.name}
            </Typography>
            {route.distanceM != null && (
              <Typography variant="caption" color="text.secondary">
                {formatDistance(route.distanceM)} ·{' '}
                {formatDuration(route.durationS)} {t('walk')}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={handleCloseSheet}
            aria-label={t('Close')}
            size="small"
          >
            <Close />
          </IconButton>
        </Box>

        {/* Map body — EXPLICIT pixel height computed from window
            size. Critical: MUI Drawer Paper isn't a flex
            container, so flex: 1 on a child doesn't expand. We
            use an explicit px height instead. MiniMap accepts
            height as a number directly. */}
        <Box
          sx={{
            width: '100%',
            height: mapBodyPx,
            position: 'relative',
            flexShrink: 0,
            bgcolor: '#e5e7eb',
          }}
        >
          <MiniMap
            destination={{
              lat: target.lat,
              lng: target.lng,
              name: target.name,
            }}
            active
            depth={0}
            interactive
            height={mapBodyPx}
          />
        </Box>

        {/* Footer — Google Maps breakout */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            display: 'flex',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Button
            variant="contained"
            fullWidth
            startIcon={<OpenInNew />}
            onClick={handleOpenMaps}
            disableElevation
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 2,
            }}
          >
            {t('Open in Google Maps')}
          </Button>
        </Box>
      </Drawer>
    </>
  );
};
